import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, LogOut, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { Logo } from "../components/Logo";
import VehiclesManager from "../components/admin/VehiclesManager";
import ServicesManager from "../components/admin/ServicesManager";
import SiteContentManager from "../components/admin/SiteContentManager";
import TestimonialsManager from "../components/admin/TestimonialsManager";
import SettingsPanel from "../components/admin/SettingsPanel";
import InboxPanel from "../components/admin/InboxPanel";
import BookingCalendar from "../components/admin/BookingCalendar";
import PartsManager from "../components/admin/PartsManager";
import OrdersPanel from "../components/admin/OrdersPanel";
import { api, formatApiError } from "../lib/api";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const STATUS_STYLES = {
  pending: "text-amber-400 border-amber-400/40 bg-amber-400/10",
  confirmed: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10",
  completed: "text-sky-400 border-sky-400/40 bg-sky-400/10",
  cancelled: "text-red-400 border-red-400/40 bg-red-400/10",
};

const inputCls = "w-full bg-[#0D1117] border border-[#263342] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-crimson transition-colors";

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
      toast.success(`Welcome back, ${data.name}`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-asphalt flex items-center justify-center px-5" data-testid="admin-login-view">
      <div className="w-full max-w-md bg-panel border border-[#263342] p-8 sm:p-10">
        <div className="flex items-center gap-3 mb-2">
          <Logo size={36} />
          <span className="font-display font-bold text-xl tracking-[0.18em] uppercase text-slate-100">Werkhaus</span>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-crimson font-semibold mt-6">RESTRICTED /// STAFF ONLY</p>
        <h1 className="font-display font-extrabold uppercase tracking-tight text-3xl text-slate-100 mt-2">Admin Login</h1>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2" htmlFor="adm-email">Email</label>
            <input id="adm-email" data-testid="admin-email-input" type="email" required className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@werkhausauto.com" />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2" htmlFor="adm-pass">Password</label>
            <input id="adm-pass" data-testid="admin-password-input" type="password" required className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
          </div>
          {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="w-full bg-crimson text-white font-display font-semibold uppercase tracking-widest py-3.5 hover:-translate-y-0.5 transition-transform disabled:opacity-60"
          >
            {loading ? "Authenticating..." : "Enter Dashboard"}
          </button>
        </form>
        <Link to="/" data-testid="admin-back-to-site" className="block mt-6 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 hover:text-slate-300 transition-colors">
          ← Back to site
        </Link>
      </div>
    </div>
  );
}

function EnquiriesPanel() {
  const [enquiries, setEnquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/enquiries");
      setEnquiries(data);
    } catch (err) {
      toast.error("Could not load enquiries", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}/status`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success(`Status updated to ${status}`);
    } catch (err) {
      toast.error("Status update failed", { description: formatApiError(err) });
    }
  };

  const counts = useMemo(() => ({
    total: enquiries.length,
    pending: enquiries.filter((e) => e.status === "pending").length,
    confirmed: enquiries.filter((e) => e.status === "confirmed").length,
    completed: enquiries.filter((e) => e.status === "completed").length,
  }), [enquiries]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enquiries.filter((e) => {
      if (filter !== "all" && e.status !== filter) return false;
      if (!q) return true;
      return [e.name, e.phone, e.email, e.service, e.vehicle_make, e.vehicle_model]
        .filter(Boolean).some((v) => v.toLowerCase().includes(q));
    });
  }, [enquiries, filter, search]);

  const stats = [
    { label: "Total Requests", value: counts.total },
    { label: "Pending", value: counts.pending },
    { label: "Confirmed", value: counts.confirmed },
    { label: "Completed", value: counts.completed },
  ];

  return (
    <>
      <div className="flex items-center justify-between">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">Incoming bookings & enquiries</p>
        <button
          data-testid="admin-refresh-button"
          onClick={load}
          className="text-slate-400 hover:text-crimson transition-colors"
          aria-label="Refresh enquiries"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
        {stats.map((s) => (
          <div key={s.label} className="bg-panel border border-[#263342] p-6">
            <p className="font-display font-extrabold text-4xl text-crimson">{s.value}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, service..."
            className={`${inputCls} pl-11`}
          />
        </div>
        <div className="flex flex-wrap gap-2" data-testid="admin-status-filters">
          {["all", ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`admin-filter-${s}`}
              onClick={() => setFilter(s)}
              className={`font-mono text-xs uppercase tracking-[0.15em] px-4 py-2.5 border transition-colors ${
                filter === s ? "border-crimson bg-crimson/10 text-crimson" : "border-[#263342] text-slate-400 hover:text-slate-200"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 bg-panel border border-[#263342] overflow-x-auto" data-testid="admin-dashboard-enquiries-table">
        {loading ? (
          <p className="p-10 text-center font-mono text-sm text-slate-500" data-testid="admin-loading">Loading enquiries...</p>
        ) : visible.length === 0 ? (
          <p className="p-10 text-center font-mono text-sm text-slate-500" data-testid="admin-empty-state">
            No enquiries yet. New booking requests will appear here.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#263342] text-left">
                {["Received", "Customer", "Vehicle", "Service", "Slot", "Status", ""].map((h) => (
                  <th key={h} className="px-5 py-4 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((e) => (
                <Fragment key={e.id}>
                  <tr
                    data-testid={`admin-enquiry-row-${e.id.slice(0, 8)}`}
                    onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                    className="border-b border-[#263342]/60 hover:bg-[#1E2733]/60 cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-4 font-mono text-xs text-slate-400 whitespace-nowrap">
                      {new Date(e.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}{" "}
                      {new Date(e.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-slate-100">{e.name}</p>
                      <p className="font-mono text-xs text-slate-500">{e.phone}</p>
                    </td>
                    <td className="px-5 py-4 text-slate-300 whitespace-nowrap">
                      {[e.vehicle_year, e.vehicle_make, e.vehicle_model].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td className="px-5 py-4 text-slate-300 max-w-[200px] truncate">{e.service}</td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-300 whitespace-nowrap" data-testid={`admin-slot-${e.id.slice(0, 8)}`}>
                      {[e.preferred_date, e.preferred_time].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td className="px-5 py-4" onClick={(ev) => ev.stopPropagation()}>
                      <select
                        data-testid={`admin-status-select-${e.id.slice(0, 8)}`}
                        value={e.status}
                        onChange={(ev) => updateStatus(e.id, ev.target.value)}
                        className={`font-mono text-xs uppercase tracking-[0.15em] border px-3 py-2 bg-transparent cursor-pointer focus:outline-none ${STATUS_STYLES[e.status] || ""}`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s} className="bg-panel text-slate-100">{s}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-slate-500">
                      {expanded === e.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </td>
                  </tr>
                  {expanded === e.id && (
                    <tr key={`${e.id}-details`} className="border-b border-[#263342]/60 bg-[#0D1117]">
                      <td colSpan={7} className="px-5 py-5" data-testid={`admin-enquiry-details-${e.id.slice(0, 8)}`}>
                        <div className="grid sm:grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Email</p>
                            <p className="text-slate-200">{e.email}</p>
                          </div>
                          <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Requested slot</p>
                            <p className="text-slate-200">{[e.preferred_date, e.preferred_time].filter(Boolean).join(" at ") || "—"}</p>
                          </div>
                          <div className="sm:col-span-2">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Notes</p>
                            <p className="text-slate-300 leading-relaxed">{e.message || "—"}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <OrdersPanel />
    </>
  );
}

function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState("enquiries");

  const tabs = [
    { id: "enquiries", label: "Service Requests" },
    { id: "calendar", label: "Calendar" },
    { id: "inbox", label: "Inbox" },
    { id: "stock", label: "Stock & Pricing" },
    { id: "reviews", label: "Reviews" },
    { id: "content", label: "Site Content" },
    { id: "settings", label: "Settings" },
  ];

  return (
    <div className="min-h-screen bg-asphalt text-slate-100" data-testid="admin-dashboard">
      <header className="border-b border-[#263342] bg-[#0D1117]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={32} />
            <span className="font-display font-bold text-lg tracking-[0.18em] uppercase">Werkhaus</span>
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.3em] text-crimson border border-crimson/40 px-2 py-1 ml-2">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-mono text-xs text-slate-500" data-testid="admin-user-email">{user.email}</span>
            <button
              data-testid="admin-logout-button"
              onClick={onLogout}
              className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-400 hover:text-crimson transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl">Control Room</h1>

        <div className="mt-6 flex gap-2 border-b border-[#263342] overflow-x-auto" data-testid="admin-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`font-mono text-xs uppercase tracking-[0.2em] px-5 py-3 border-b-2 -mb-px whitespace-nowrap transition-colors ${
                tab === t.id ? "border-crimson text-crimson" : "border-transparent text-slate-500 hover:text-slate-200"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "enquiries" && <EnquiriesPanel />}
          {tab === "calendar" && (
            <div data-testid="admin-calendar-panel">
              <BookingCalendar />
            </div>
          )}
          {tab === "inbox" && (
            <div data-testid="admin-inbox-panel">
              <InboxPanel />
            </div>
          )}
          {tab === "stock" && (
            <div data-testid="admin-stock-panel">
              <VehiclesManager />
              <ServicesManager />
              <PartsManager />
            </div>
          )}
          {tab === "reviews" && (
            <div data-testid="admin-reviews-panel">
              <TestimonialsManager />
            </div>
          )}
          {tab === "content" && (
            <div data-testid="admin-content-panel">
              <SiteContentManager />
            </div>
          )}
          {tab === "settings" && (
            <div data-testid="admin-settings-panel">
              <SettingsPanel />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
    toast.success("Logged out");
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-asphalt flex items-center justify-center" data-testid="admin-loading-auth">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-500">Checking credentials...</p>
      </div>
    );
  }

  return user ? <Dashboard user={user} onLogout={logout} /> : <LoginForm onLogin={setUser} />;
}
